import { ChangeEvent, ComponentPropsWithoutRef, DragEvent } from 'react';
import { useFormikContext } from 'formik';

import { Button, ControlGroup, Input, Icon } from '@hs-baumappe/legacy-ui';

type UploadInputGroupProps = Omit<ComponentPropsWithoutRef<'input'>, 'size' | 'color'> & {
  name?: string;
  invalidFileTypeError?: string;
};

function isFileTypeValid(file: File, accept: string): boolean {
  const acceptedTypes = accept.split(',').map((type) => type.trim());

  return acceptedTypes.some((acceptedType) => {
    if (acceptedType.includes('/')) {
      return file.type === acceptedType;
    }
    if (acceptedType.startsWith('.')) {
      return file.name.toLowerCase().endsWith(acceptedType.toLowerCase());
    }
    return false;
  });
}

export default function UploadInputGroup(inputProps: UploadInputGroupProps): JSX.Element {
  const { accept, onChange, onDrop, name, invalidFileTypeError, ...restInputProps } = inputProps;
  const formik = useFormikContext();

  const setFieldError = (error: string) => {
    if (name) {
      formik.setFieldError(name, error);
      formik.setFieldTouched(name, true, false);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (accept && file) {
      if (!isFileTypeValid(file, accept)) {
        // eslint-disable-next-line no-param-reassign
        event.target.value = '';
        if (invalidFileTypeError) {
          setFieldError(invalidFileTypeError);
        }
        return;
      }
    }

    if (onChange) {
      onChange(event);
    }
  };

  const handleDrop = (event: DragEvent<HTMLInputElement>) => {
    const file = event.dataTransfer.files[0];

    if (accept && file) {
      if (!isFileTypeValid(file, accept)) {
        event.preventDefault();
        event.stopPropagation();
        if (invalidFileTypeError) {
          setFieldError(invalidFileTypeError);
        }
        return;
      }
    }

    if (onDrop) {
      onDrop(event);
    }
  };

  return (
    <ControlGroup>
      <Input
        type="file"
        accept={accept}
        onChange={handleChange}
        onDrop={handleDrop}
        {...restInputProps}
      />
      <Button
        component="label"
        htmlFor={inputProps.id}
        color="primary"
        contentClassName="u-display-flex"
      >
        <Icon name="folder" size={24} />
      </Button>
    </ControlGroup>
  );
}
